import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { RutField, fillEmpty, splitName } from '../RutField';

const input = 'w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500';
const empty = { notary_name: '', comuna: '', verify_url: '', verify_code: '', signer: {} };

export const useNotaryProfile = () => {
  const { api } = useAuth();
  const [profile, setProfile] = useState(empty);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get('/notary-profile')
      .then((r) => setProfile({ ...empty, ...r.data, signer: r.data.signer || {} }))
      .catch(() => toast.error('No se pudieron cargar los datos de la notaría'))
      .finally(() => setLoaded(true));
  }, [api]);
  return [profile, setProfile, loaded];
};

export const NotaryProfileForm = ({ profile, setProfile, compact = false, loaded = true }) => {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setProfile({ ...profile, [k]: v });
  const setS = (k, v) => setProfile({ ...profile, signer: { ...profile.signer, [k]: v } });

  const save = async () => {
    setBusy(true);
    try {
      const signer = { ...profile.signer, rut: (profile.signer.rut || '').replace(/\./g, ''), email: profile.signer.email || null };
      const r = await api.put('/notary-profile', { ...profile, signer });
      setProfile({ ...empty, ...r.data });
      toast.success('Datos de la notaría guardados');
    } catch (e) {
      toast.error(Array.isArray(e.response?.data?.detail) ? 'Revisa el RUT (12345678-9) y el email' : 'No se pudieron guardar');
    } finally { setBusy(false); }
  };
  const f = (k, ph, v, on) => <input data-testid={`notary-${k}`} disabled={!loaded} value={v || ''} onChange={(e) => on(e.target.value)} placeholder={ph} className={input} />;

  return (
    <div data-testid="notary-profile-form" className="space-y-2">
      <div className="grid sm:grid-cols-2 gap-2">
        {f('name', 'Nombre del notario', profile.notary_name, (v) => set('notary_name', v))}
        {f('comuna', 'Comuna de la notaría', profile.comuna, (v) => set('comuna', v))}
        {f('verify-url', 'Dirección web de verificación', profile.verify_url, (v) => set('verify_url', v))}
        {f('verify-code', 'Código de verificación', profile.verify_code, (v) => set('verify_code', v))}
      </div>
      <p className="text-xs uppercase tracking-wider text-blue-400 font-semibold pt-2">Firmante en FirmaWeb</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {f('signer-nombre', 'Nombre (firmante FirmaWeb)', profile.signer.nombre, (v) => setS('nombre', v))}
        {f('signer-paterno', 'Apellido paterno', profile.signer.apellidoPaterno, (v) => setS('apellidoPaterno', v))}
        {f('signer-materno', 'Apellido materno', profile.signer.apellidoMaterno, (v) => setS('apellidoMaterno', v))}
        <RutField testid="notary-signer-rut" disabled={!loaded} value={profile.signer.rut} onChange={(v) => setS('rut', v)} placeholder="RUT (12.345.678-9)" className={input}
          onLookup={(d) => { const n = splitName(d.name); setProfile((p) => ({ ...p, signer: fillEmpty(p.signer, { nombre: n.nombre, apellidoPaterno: n.paterno, apellidoMaterno: n.materno }) })); }} />
        <div className="sm:col-span-2">{f('signer-email', 'Email del notario', profile.signer.email, (v) => setS('email', v))}</div>
      </div>
      <button data-testid="notary-save-btn" onClick={save} disabled={busy || !loaded} className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-full px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Guardar como predeterminados
      </button>
    </div>
  );
};
